import { type Vendor } from '@kalos/kalos-rpc';
import { Button, FormControl, FormItem, FormLabel, FormMessage } from '@kalos/ui';
import { ChevronDown } from 'lucide-react';
import { type FC, useCallback, useMemo, useRef, useState } from 'react';

import { useVendorBatchQuery } from '../../../../hooks/react-query/useVendorQuery';
import { VendorSelectionModal } from './VendorSelectionModal';

interface VendorFieldProps {
  value: number;
  onChange: (vendorId: number) => void;
  disabled?: boolean;
}

export const VendorField: FC<VendorFieldProps> = ({ value, onChange, disabled = false }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const vendorsQuery = useVendorBatchQuery({
    filter: {
      isActive: 1,
      withoutLimit: true,
      orderBy: 'vendor_name',
    },
  });

  const selectedVendor = useMemo(() => {
    if (value === 0) return null;
    return vendorsQuery.data?.results.find((vendor) => vendor.id === value) || null;
  }, [value, vendorsQuery.data?.results]);

  const displayText = useMemo(() => {
    if (value === 0 || !selectedVendor) {
      return 'Select Vendor...';
    }
    return selectedVendor.vendorName;
  }, [value, selectedVendor]);

  const handleOpenModal = useCallback(() => {
    if (!disabled) {
      setIsModalOpen(true);
    }
  }, [disabled]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        setIsModalOpen(true);
      }
    },
    [disabled],
  );

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    // Return focus to the button after modal closes
    setTimeout(() => {
      buttonRef.current?.focus();
    }, 100);
  }, []);

  const handleSelectVendor = useCallback(
    (vendor: Vendor) => {
      onChange(vendor.id);
    },
    [onChange],
  );

  return (
    <FormItem>
      <FormLabel>Vendor</FormLabel>
      <FormMessage />
      <FormControl>
        <Button
          ref={buttonRef}
          type="button"
          variant="outline"
          onClick={handleOpenModal}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          className="w-full justify-between text-left font-normal"
        >
          <span className={selectedVendor ? 'text-foreground' : 'text-muted-foreground'}>
            {displayText}
          </span>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </FormControl>

      <VendorSelectionModal
        open={isModalOpen}
        onClose={handleCloseModal}
        onSelect={handleSelectVendor}
        selectedVendorId={value}
      />
    </FormItem>
  );
};
